import {
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type {
  ChangeEvent,
  RenderInputComponentProps,
  RenderSuggestionsContainerParams,
} from 'react-autosuggest';
import Autosuggest from 'react-autosuggest';

import { AutosuggestWrapper } from '#/components/Autocomplete/Autocomplete.styled';
import type {
  InputAffixVariant,
  InputStyleVariant,
} from '#/components/Input/Input.types';
import { LoadingSection } from '#/components/LoadingSection/LoadingSection';
import { useDebouncedFn } from '#/hooks/useDebouncedFn';
import { useIsFirstRenderRef } from '#/hooks/useIsFirstRenderRef';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import { REMOTE_KEYWORDS } from '#/utils/constants/search';
import { useGMapsErrored } from '#/zustand-stores/gMapsErrorStore';
import {
  fetchGoogleMapsPlaceDetails,
  loadGoogleMapsLibraries,
  useGoogleMapsAutocomplete,
} from '#/zustand-stores/googleMapsLibrariesStore';

import { LocationInputSuggestion } from './LocationInputSuggestion';
import type { LocationSuggestion } from './LocationSuggestion';
import { StyledInput } from './StyledInput';
import { SuggestionsContainer } from './SuggestionsContainer';
import { geocoderResultToSearchLocation } from './geocoderResultToSearchLocation';
import { getRemoteOption } from './getRemoteOption';

type Props = {
  onChange: (searchLocation: SearchLocation | undefined) => void;
  onClear?: () => void;
  hasSuccess?: boolean;
  qaId?: string;
  placeholder?: string;
  required?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  showLocationIcon?: boolean;
  showClearButton?: boolean;
  className?: string;
  handlePlaceDetails?: (place: google.maps.GeocoderResult) => void;
  addressTypes?: string[];
  includeCity?: boolean;
  includeFull?: boolean;
  disabled?: boolean;
  hasRemoteOptions?: boolean;
  highlightFirstSuggestion?: boolean;
  includedRegionCodes?: string[];
  affixVariant?: InputAffixVariant;
  styleVariant?: InputStyleVariant;
  locationText: string | undefined | null;
  id: string;
};

export function LocationInput({
  hasSuccess,
  onChange,
  onClear,
  placeholder,
  required,
  inputRef: customInputRef,
  showLocationIcon = false,
  showClearButton,
  className,
  addressTypes,
  includeCity,
  includeFull,
  disabled,
  highlightFirstSuggestion = true,
  includedRegionCodes,
  hasRemoteOptions,
  locationText,
  handlePlaceDetails: handlePlaceDetailsProp,
  qaId,
  id,
  affixVariant,
  styleVariant,
}: Props) {
  const { i18n, locale } = useI18n();

  const [defaultKeyword] = REMOTE_KEYWORDS[locale];
  const defaultInputRef = useRef<HTMLInputElement>(null);
  const inputRef = customInputRef ?? defaultInputRef;

  const [suggestionsTuple, setSuggestionsTuple] = useState<{
    input: string;
    suggestions: LocationSuggestion[];
  } | null>(null);

  const { gMapsErrored } = useGMapsErrored();

  const [locationInputValue, setLocationInputValue] = useKeyedState(
    locationText ?? '',
    { key: locationText ?? '' },
  );

  const { googleMapsAutocomplete, abortGoogleMapsAutocomplete } =
    useGoogleMapsAutocomplete();

  const debouncedLoadSuggestions = useDebouncedFn(async (input: string) => {
    const result = await googleMapsAutocomplete({
      input,
      includedRegionCodes,
      addressTypes,
    });

    if (result.type === 'aborted') return;

    const options = result.suggestions;

    const remoteOptions: (LocationSuggestion | undefined)[] = [];

    if (hasRemoteOptions) {
      remoteOptions.push(
        getRemoteOption({ locale, value: input, defaultKeyword }),
      );
    }

    setSuggestionsTuple({
      input,
      suggestions: [...remoteOptions, ...(options || [])].filter(Boolean),
    });
  }, 300);

  const clearLocation = useCallback(() => {
    setLocationInputValue('');
    onClear?.();
    onChange(undefined);
    setSuggestionsTuple(null);
    abortGoogleMapsAutocomplete();
  }, [onChange, onClear, setLocationInputValue, abortGoogleMapsAutocomplete]);

  const onLocationInputValueChange = (
    _: FormEvent<unknown>,
    { newValue }: ChangeEvent,
  ) => {
    if (newValue === '') clearLocation();
    setLocationInputValue(newValue);
  };

  const onBlur = () => {
    if (locationInputValue && locationInputValue !== locationText) {
      clearLocation();
      setSuggestionsTuple(null);
      abortGoogleMapsAutocomplete();
    }
  };

  const handlePlaceDetails = useCallback(
    async (place: google.maps.GeocoderResult) => {
      // Use the formatted address, in rare cases where a suggested place isn't
      // formatted identically to its geocoded version.
      setLocationInputValue(place.formatted_address);

      if (handlePlaceDetailsProp) {
        handlePlaceDetailsProp(place);
        return;
      }

      await geocoderResultToSearchLocation(place, {
        includeCity,
        includeFull,
      }).then(onChange);
    },
    [
      setLocationInputValue,
      handlePlaceDetailsProp,
      includeCity,
      includeFull,
      onChange,
    ],
  );

  const onSuggestionSelected = useCallback(
    async (
      _: FormEvent<HTMLInputElement>,
      { suggestion }: { suggestion: LocationSuggestion },
    ) => {
      abortGoogleMapsAutocomplete();

      if (suggestion.isRemoteOption) {
        onChange({
          text: null,
          stateCode: null,
          countryCode: null,
          boundingBoxN: null,
          boundingBoxW: null,
          boundingBoxS: null,
          boundingBoxE: null,
          geoType: null,
          latitude: null,
          longitude: null,
          isRemote: true,
          timezone: null,
        });

        return;
      }

      const placeId = suggestion.placePrediction?.placeId;
      if (placeId) {
        const placeDetails = await fetchGoogleMapsPlaceDetails({
          placeId,
          language: locale,
        });
        if (placeDetails) void handlePlaceDetails(placeDetails);
      }
    },
    [locale, handlePlaceDetails, onChange, abortGoogleMapsAutocomplete],
  );

  const onSuggestionsFetchRequested = useCallback(
    ({ value }: { value: string }) => {
      debouncedLoadSuggestions(value);
    },
    [debouncedLoadSuggestions],
  );

  const renderSuggestion = useCallback(
    (suggestion: LocationSuggestion) => (
      <LocationInputSuggestion suggestion={suggestion} />
    ),
    [],
  );

  const onSuggestionsClearRequested = useCallback(() => {
    setSuggestionsTuple({ input: locationInputValue, suggestions: [] });
    abortGoogleMapsAutocomplete();
  }, [locationInputValue, abortGoogleMapsAutocomplete]);

  const { isFirstRenderRef } = useIsFirstRenderRef();
  useEffect(() => {
    const inputValue = inputRef.current?.value ?? '';
    if (inputValue) setLocationInputValue(inputValue);

    if (
      isFirstRenderRef.current &&
      inputRef.current === document.activeElement &&
      inputValue &&
      !suggestionsTuple
    ) {
      // load when focused
      debouncedLoadSuggestions(inputValue);
    }
  }, [
    isFirstRenderRef,
    suggestionsTuple,
    debouncedLoadSuggestions,
    inputRef,
    setLocationInputValue,
  ]);

  const renderSuggestionsContainer = ({
    containerProps,
    children,
    query,
  }: RenderSuggestionsContainerParams) => {
    // Skip rendering container during SSR
    if (!inputRef.current) return null;

    const isFocused = document.activeElement === inputRef.current;

    const inputHasMinimumLength = query.length >= 2;

    const previousSuggestionsNotDisplaying =
      !suggestionsTuple ||
      suggestionsTuple.input.length < 2 ||
      !suggestionsTuple.suggestions;

    const noResultsForQuery = suggestionsTuple?.input !== query;

    const notCurrentLocation = locationText !== query;

    const showLoading =
      isFocused &&
      inputHasMinimumLength &&
      previousSuggestionsNotDisplaying &&
      noResultsForQuery &&
      notCurrentLocation;

    if (!showLoading && !children) return null;

    return (
      <SuggestionsContainer
        containerProps={containerProps}
        loading={showLoading}
      >
        {showLoading ? <LoadingSection noDelay /> : children}
      </SuggestionsContainer>
    );
  };

  const renderInputComponent = useCallback(
    ({
      ref,
      autoComplete,
      className: inputClassName,
      disabled: inputDisabled,
      onBlur: inputOnBlur,
      onChange: inputOnChange,
      onFocus,
      onKeyDown,
      onKeyPress,
      title,
      value,
      ...renderInputComponentProps
    }: RenderInputComponentProps) => (
      <StyledInput
        aria-activedescendant={
          renderInputComponentProps['aria-activedescendant']
        }
        aria-autocomplete={renderInputComponentProps['aria-autocomplete']}
        aria-controls={renderInputComponentProps['aria-controls']}
        aria-label={i18n('Location')}
        autoComplete={autoComplete}
        className={inputClassName}
        disabled={inputDisabled}
        onBlur={inputOnBlur}
        onChange={inputOnChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        hasSuccess={hasSuccess}
        onKeyPress={onKeyPress}
        placeholder={placeholder}
        required={required}
        title={title}
        value={value}
        onClear={showClearButton ? clearLocation : null}
        gMapsErrored={gMapsErrored}
        showLocationIcon={showLocationIcon}
        styleVariant={styleVariant}
        affixVariant={affixVariant}
        qaId={qaId ?? 'location-input'}
        id={id}
        inputRef={ref}
      />
    ),
    [
      i18n,
      clearLocation,
      gMapsErrored,
      hasSuccess,
      placeholder,
      required,
      qaId,
      affixVariant,
      styleVariant,
      showClearButton,
      showLocationIcon,
      id,
    ],
  );

  const onKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }, []);

  const getSuggestionValue = useCallback(
    (suggestion: LocationSuggestion) =>
      suggestion.placePrediction?.text.text || '',
    [],
  );

  return (
    <AutosuggestWrapper>
      <Autosuggest
        highlightFirstSuggestion={highlightFirstSuggestion}
        suggestions={suggestionsTuple?.suggestions ?? []}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={(v) =>
          Boolean(v && v.toString().trim().length >= 2)
        }
        renderInputComponent={renderInputComponent}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={{
          className,
          disabled,
          onChange: onLocationInputValueChange,
          onBlur,
          onFocus: () => {
            void loadGoogleMapsLibraries();
          },
          onKeyPress,
          placeholder,
          required,
          value: locationInputValue,
          title: i18n('Location'),
          ref: inputRef,
        }}
      />
    </AutosuggestWrapper>
  );
}
